import { useMemo, useState } from 'react'

import {
  Box,
  Spinner,
  Text,
  TogglerCheckbox,
  Tooltip,
  WizardStepRenderParams,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { SettingsStepDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import EsignatureSettings, {
  SettingsValues,
} from 'modules/documents/components/BulkFiles/_steps/EsignatureSettings'
import FolderSelector from 'modules/documents/components/Folders/FolderSelector'
import CustomFields from 'modules/documents/components/UploadFormWizard/CustomFields'
import { WizardData } from 'modules/documents/components/UploadFormWizard/types'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import {
  isCompanyInternalSpace,
  isCompanyPublicSpace,
  isCompanySpace,
} from 'modules/documents/lib/Documents'
import { ESIGNATURE_FILE_TYPE, SPACE, SPACES } from 'modules/documents/lib/constants'
import { buildCustomFieldForms } from 'modules/documents/lib/forms/CustomFields/BuildForms'

interface Props {
  folders: Folder[]
  employeeFirstName: string
  space: SPACE
  showVisibility?: boolean
  submitting?: boolean
  canUseCompanyDocuments: boolean
  defaultSigner?: string
}

const Settings = ({
  data,
  onDataChanged,
  folders,
  employeeFirstName: firstName,
  showVisibility = false,
  submitting = false,
  space = SPACES.EMPLOYEE_MY_DOCUMENTS,
  canUseCompanyDocuments,
  defaultSigner,
}: WizardStepRenderParams<WizardData> & Props) => {
  const {
    files,
    esignatureRequest,
    additionalSignersAccessIds,
    hidden,
    folderId,
    signaturePlacement,
    employeeId,
  } = data

  const { data: settingsStepData, loading } = useQuery(SettingsStepDocument, {
    variables: { folderId, employeeId: employeeId || '-1' },
  })
  const areSignableFiles =
    files.length !== 0 &&
    files.filter((file) => file.type === ESIGNATURE_FILE_TYPE).length === files.length
  const currentFolder = folders.find((f) => f.id === folderId)

  const canEditEmployeeFolderForEverybody =
    settingsStepData?.documents?.editableEmployeeFoldersConnection?.nodes.find(
      (f) => f.id === folderId
    )?._policies?.editEmployeeFolderForEverybody?.can ?? false

  const canRequestEsignatures =
    (canUseCompanyDocuments || canEditEmployeeFolderForEverybody) && !isCompanySpace(space)

  const isEmployeeSpace = space === SPACES.EMPLOYEE_MY_DOCUMENTS
  const isCompanySpaceCheck = isCompanySpace(space)
  const [customFieldsForms, setCustomFieldsForms] = useState<Form[]>([])
  const customFields = settingsStepData?.customFields.fieldsConnection.nodes || []

  const forms = folderId && customFields.length > 0 ? buildCustomFieldForms(customFields) : []
  const [openVisibility, toggleVisibility] = useToggle()
  const withoutFolderPermission =
    settingsStepData?.documents?._policies?.withoutFolderEverybodyEditEmployeeDocumentsWithoutFolder
      ?.can

  let canEditNoFolder =
    (settingsStepData?.employees?.employeesConnection?.nodes?.[0]?._policies
      ?.editEmployeeDocumentsWithoutFolder?.can ||
      settingsStepData?.employees?.employeesConnection?.nodes?.[0]?._policies?.editDocuments
        ?.can) ??
    false

  if (isCompanySpaceCheck) {
    if (isCompanyPublicSpace(space)) {
      canEditNoFolder =
        settingsStepData?.documents?._policies?.companyEditPublicDocuments?.can ?? false
    } else {
      canEditNoFolder =
        settingsStepData?.documents?._policies?.companyEditInternalDocuments?.can ?? false
    }
  }

  useUnsafeEffect(() => {
    onDataChanged({
      ...data,
      canEditEmployeeNoFolder: canEditNoFolder,
    })
  }, [canEditNoFolder])

  useUnsafeEffect(() => {
    setCustomFieldsForms(forms)
    onDataChanged({ ...data, customFields: forms, canEditEmployeeNoFolder: canEditNoFolder })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId, customFields])

  useUnsafeEffect(() => {
    if (!areSignableFiles) {
      onDataChanged({
        ...data,
        esignatureRequest: false,
        additionalSignersAccessIds: [],
        signaturePlacement: false,
        canEditEmployeeNoFolder: canEditNoFolder,
      })
    }
  }, [areSignableFiles])

  const onChangeFolder = (newFolderId: string | null) => {
    onDataChanged({ ...data, folderId: newFolderId })
  }

  const onChange = (settings: SettingsValues) => {
    const { newEsignatureRequest, newAdditionalSignersAccessIds, newSignaturePlacement } = settings

    onDataChanged({
      ...data,
      esignatureRequest: newEsignatureRequest,
      additionalSignersAccessIds: newEsignatureRequest ? newAdditionalSignersAccessIds : [],
      signaturePlacement: newSignaturePlacement ?? false,
      hidden: false,
      canEditEmployeeNoFolder: canEditNoFolder,
    })
  }

  const signaturePermissions = useMemo(() => {
    let permission = false

    if (isCompanyPublicSpace(space)) {
      permission = false
    } else if (isCompanyInternalSpace(space)) {
      permission =
        settingsStepData?.documents?._policies?.companyEditInternalDocuments?.can ?? false
    } else {
      if (folderId) {
        permission =
          folders.find((f) => f.id === folderId)?._policies?.editEmployeeFolderForEverybody?.can ??
          false
      } else {
        permission = withoutFolderPermission ?? false
      }
    }

    return canRequestEsignatures || permission
  }, [folderId, folders, withoutFolderPermission, canRequestEsignatures])

  if (submitting || loading) {
    return (
      <Box alignItems="center" justifyContent="center" padding="s12">
        <Spinner />
      </Box>
    )
  }

  return (
    <Box flexDirection="column" alignItems="center" gap="s24">
      <Box width="s600" gap="s16">
        <FolderSelector
          initialFolder={currentFolder}
          initialSpace={space}
          folders={folders}
          label={i18n.t('split_documents.form.select_label')}
          onChange={onChangeFolder}
          noneOption={canEditNoFolder}
        />

        {showVisibility && (
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
            padding="s16"
            borderRadius={{ all: 'abs016' }}
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <Box gap="s12" width="full" justifyContent="spaceBetween" flexDirection="row">
              <Text.Normal weight="semibold">
                {i18n.t('documents.beta.hide_files_title', {
                  firstName: isEmployeeSpace ? firstName : i18n.t('common.employees').toLowerCase(),
                })}
              </Text.Normal>
              <Tooltip.Alt
                attachment="bottom center"
                open={esignatureRequest && openVisibility}
                toggle={toggleVisibility}
                label="Only visible files can be signed"
              >
                <TogglerCheckbox
                  value={hidden}
                  labelWeight={'medium'}
                  onChange={(value) => onDataChanged({ ...data, hidden: value })}
                  disabled={esignatureRequest}
                />
              </Tooltip.Alt>
            </Box>
          </Box>
        )}
        {hidden && (
          <Box
            borderRadius={{ all: 'abs016' }}
            padding="s24"
            alignItems="center"
            background="tangerine200"
          >
            <Text size="200" color="black">
              {isEmployeeSpace
                ? i18n.t('documents.beta.hide_files_description', { firstName })
                : i18n.t('documents.beta.hide_files_public_description', { firstName })}
            </Text>
          </Box>
        )}
        {signaturePermissions && (
          <EsignatureSettings
            settings={{
              newEsignatureRequest: esignatureRequest,
              newAdditionalSignersAccessIds: additionalSignersAccessIds,
              newSignaturePlacement: signaturePlacement,
              hidden: hidden,
            }}
            onChange={onChange}
            showRecipient={false}
            showSignaturePlacement
            defaultSigner={defaultSigner}
            signableFiles={areSignableFiles}
          />
        )}
        {customFieldsForms.length > 0 && <CustomFields forms={customFieldsForms} />}
      </Box>
    </Box>
  )
}

export default Settings
